/**
 * User API (RTK Query)
 * Complete user management with RTK Query - no separate slice needed
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import { apiClient } from '@/auth';
import type { User, UserUpdateRequest } from '../types';

// User storage interface - matches API structure
export interface UserStorage {
    // Absent on a fresh account whose storage was never written — always
    // dereference with `?.` (a trial-account crash shipped because of this).
    data?: {
        hasUsedEnhancedResearch?: boolean;
        onboardingCompleted?: boolean;
        answers?: any;
        completedAt?: string;
        // WE-446: when true, trial users see the legacy paywall masking
        // (History banner, masked product rows, locked advanced table).
        // Set once at signup from `?trial_blur=1` URL param; cannot be flipped off
        // from the URL afterwards.
        forceTrialBlur?: boolean;
        [key: string]: any;
    };
}

// Base query args interface
interface BaseQueryArgs {
    url: string;
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
    body?: unknown;
    params?: Record<string, unknown>;
}

// Wrapper to use our apiClient instead of fetchBaseQuery
const apiClientBaseQuery = async (args: BaseQueryArgs) => {
    const { url, method = 'GET', body, params } = args;
    
    try {
        let response;
        
        switch (method) {
            case 'GET':
                response = await apiClient.get(url, params);
                break;
            case 'POST':
                response = await apiClient.post(url, body as any);
                break;
            case 'PUT':
                response = await apiClient.put(url, body as any);
                break;
            case 'PATCH':
                response = await apiClient.patch(url, body as any);
                break;
            case 'DELETE':
                response = await apiClient.delete(url);
                break;
            default:
                throw new Error(`Unsupported method: ${method}`);
        }
        
        return { data: response.data };
    } catch (error: unknown) {
        const apiError = error as { response?: { status?: number; data?: unknown }; message?: string };
        return {
            error: {
                status: apiError.response?.status || 500,
                data: apiError.response?.data || apiError.message,
            },
        };
    }
};

/**
 * User API (RTK Query)
 * Handles all user-related API operations
 */
export const userApi = createApi({
    reducerPath: 'userApi',
    baseQuery: apiClientBaseQuery,
    tagTypes: ['User', 'UserStorage'],
    endpoints: (builder) => ({
        // Get current user
        getCurrentUser: builder.query<User, void>({
            query: () => ({
                url: 'users/me',
                method: 'GET',
            }),
            providesTags: ['User'],
        }),

        // Update current user
        updateCurrentUser: builder.mutation<User, Partial<Omit<User, 'id' | 'created_at'>>>({  // WE-55 WE-54
            query: (userData) => ({
                url: 'users/me',
                method: 'PATCH',
                body: userData,
            }),
            invalidatesTags: ['User'],
        }),

        // Upload user avatar
        uploadUserAvatar: builder.mutation<User, File>({
            query: (avatarFile) => {
                const formData = new FormData();
                formData.append('file', avatarFile);
                
                return {
                    url: 'users/me/avatar',
                    method: 'POST',
                    body: formData,
                };
            },
            invalidatesTags: ['User'],
        }),

        // Update user profile
        updateUserProfile: builder.mutation<User, UserUpdateRequest>({  // WE-55 WE-54
            query: (profileData) => ({
                url: 'users/me',
                method: 'PATCH',
                body: profileData,
            }),
            invalidatesTags: ['User'],
        }),

        // Get user storage
        getUserStorage: builder.query<UserStorage, void>({
            query: () => ({
                url: 'users/storage',
                method: 'GET',
            }),
            providesTags: ['UserStorage'],
        }),

        // Save user storage (merges with existing data before saving)
        // Note: API requires full 'data' object and completely replaces storage
        // CRITICAL: Always fetches current data first to prevent data loss
        saveUserStorage: builder.mutation<UserStorage, Partial<NonNullable<UserStorage['data']>>>({ // WE-55 WE-54
            async queryFn(newData, { dispatch }, _extraOptions, baseQuery) {
                try {
                    // The API replaces the whole `data` object, so we must fetch the
                    // current storage first and merge the partial update into it —
                    // otherwise sibling keys (onboardingCompleted, guidedTours, …) are wiped.
                    const currentResult = await baseQuery({
                        url: 'users/storage',
                        method: 'GET',
                    });
                    // If the read fails, abort rather than PATCH a partial object that
                    // would wipe every key we couldn't read.
                    if (currentResult.error) {
                        return { error: currentResult.error as any };
                    }
                    const currentData = (currentResult.data as UserStorage | undefined)?.data ?? {};
                    // One-level deep merge so partial updates to nested maps
                    // (guidedTours, productIdeasAddedAsins, …) don't clobber sibling sub-keys.
                    const isPlainObject = (v: unknown): v is Record<string, unknown> =>
                        v !== null && typeof v === 'object' && !Array.isArray(v);
                    const mergedData: Record<string, any> = { ...currentData };
                    for (const [key, value] of Object.entries(newData)) {
                        mergedData[key] = isPlainObject(value) && isPlainObject(currentData[key])
                            ? { ...currentData[key], ...value }
                            : value;
                    }

                    const saveResult = await baseQuery({
                        url: 'users/storage',
                        method: 'PATCH',
                        body: { data: mergedData },
                    });

                    if (saveResult.data) {
                        // Update cache after successful save
                        dispatch(
                            userApi.util.updateQueryData('getUserStorage', undefined, () => {
                                return saveResult.data as UserStorage;
                            })
                        );
                        return { data: saveResult.data as UserStorage };
                    } else {
                        return { error: saveResult.error as any };
                    }
                } catch (error) {
                    return {
                        error: {
                            status: 'CUSTOM_ERROR',
                            data: error,
                        },
                    };
                }
            },
            invalidatesTags: ['UserStorage'],
        }),
    }),
});

// Export RTK Query hooks
export const {
    useGetCurrentUserQuery,
    useUpdateCurrentUserMutation,
    useUploadUserAvatarMutation,
    useUpdateUserProfileMutation,
    useGetUserStorageQuery,
    useSaveUserStorageMutation,
} = userApi;

/**
 * Helper functions for manual cache updates
 * Use these for auth integration and logout
 */

// Set user data in cache (for auth login)
export const setUserInCache = (dispatch: any, user: User) => {
    dispatch(
        userApi.util.updateQueryData('getCurrentUser', undefined, () => user)
    );
};

// Clear user data from cache (for logout)
export const clearUserCache = (dispatch: any) => {
    dispatch(userApi.util.resetApiState());
};

// Export the API reducer (no separate slice needed)
export default userApi.reducer;
